import {
  normalizeRecordings,
  getCurrentRecording,
  getFilteredRecordingKeys,
  getUpdatedStatsOnToggleRecordingKey,
  getUpdatedStatsOnToggleAllRecordingKeys,
  computeRecordingStats
} from './RecordingsUtils';
import { MAX_RANGE, MIN_RANGE, RecordingSource } from '../../Utils/Constants';
import { getDifference, getUnion, sortArrayBasedOnArray } from '../../Utils/ArrayUtils';
import {
  FILTER_RECORDINGS_BY_COUNT,
  FILTER_RECORDINGS_BY_COUNT_REQUEST,
  GET_RECORDINGS_FAILURE,
  GET_RECORDINGS_REQUEST,
  GET_RECORDINGS_SUCCESS,
  TOGGLE_ALL_RECORDINGS,
  TOGGLE_RECORDING,
  RECORDINGS_POST_UNIQUE_TABLE_ID
} from './RecordingsActions';

const initialState = {
  fetching: false,
  filtering: false,
  recordings: {
    byKey: {},
    allKeys: [],
    deSelectedKeys: [],
    filteredKeys: [],
    filteredSelectedRecordingKeys: []
  },
  filterRecordings: {
    byKey: {},
    allKeys: [],
    deSelectedKeys: [],
    filteredKeys: [],
    filteredSelectedRecordingKeys: []
  },
  uniqueTableId: '',
  filter: {
    fromTime: null,
    toTime: null,
    searchString: '',
    sources: [RecordingSource.G2W, RecordingSource.G2S, RecordingSource.G2S_EMBEDDED],
    viewsRange: [MIN_RANGE, MAX_RANGE],
    sharesRange: [MIN_RANGE, MAX_RANGE]
  },
  stats: {
    filteredTotalVideos: 0,
    filteredG2STotalVideos: 0,
    filteredStandaloneTotalVideos: 0,
    filteredG2STotalViews: 0,
    filteredStandaloneTotalViews: 0,
    filteredG2STotalShares: 0,
    filteredSelectedTotalVideos: 0,
    filteredSelectedG2STotalVideos: 0,
    filteredSelectedStandaloneTotalVideos: 0,
    filteredSelectedG2STotalViews: 0,
    filteredSelectedStandaloneTotalViews: 0,
    filteredSelectedG2STotalShares: 0
  },
  filterRecordingStats: {
    filteredTotalVideos: 0,
    filteredG2STotalVideos: 0,
    filteredStandaloneTotalVideos: 0,
    filteredG2STotalViews: 0,
    filteredStandaloneTotalViews: 0,
    filteredG2STotalShares: 0,
    filteredSelectedG2STotalVideos: 0,
    filteredSelectedTotalVideos: 0,
    filteredSelectedStandaloneTotalVideos: 0,
    filteredSelectedG2STotalViews: 0,
    filteredSelectedG2STotalShares: 0,
    filteredSelectedStandaloneTotalViews: 0
  },
  error: null
};

export default (state = initialState, action = {}) => {
  switch (action.type) {
    case RECORDINGS_POST_UNIQUE_TABLE_ID: {
      const { uniqueTableId } = action.payload;
      return {
        ...state,
        uniqueTableId: uniqueTableId
      };
    }

    case GET_RECORDINGS_REQUEST: {
      return {
        ...state,
        filter: {
          ...state.filter,
          fromTime: action.payload.fromTime,
          toTime: action.payload.toTime
        },
        fetching: true,
        error: null
      };
    }

    case GET_RECORDINGS_SUCCESS: {
      const recordingsList = action.payload.recordingsList;
      const { allKeys, byKey } = normalizeRecordings(recordingsList);
      const {
        viewsRange = state.filter.viewsRange,
        sharesRange = state.filter.sharesRange,
        searchString = state.filter.searchString,
        fromTime = state.filter.fromTime,
        toTime = state.filter.toTime,
        sources = state.filter.sources
      } = action.payload;

      let recordings = state.recordings;
      const filteredKeys = getFilteredRecordingKeys(byKey, viewsRange, sharesRange, searchString, sources, allKeys);
      const newFilterSelectedRecordingKeys = getDifference(filteredKeys, recordings.deSelectedKeys);

      const filteredStats = computeRecordingStats(byKey, filteredKeys);
      const filteredSelectedStats = computeRecordingStats(byKey, newFilterSelectedRecordingKeys);

      let recordingsFilter = state.filterRecordings;
      const newFilterDeSelectedRecordingKeys = getDifference(
        filteredKeys,
        recordingsFilter.filteredSelectedRecordingKeys
      );
      const newFilterDeSelectedRecordingKeysSecond = getDifference(filteredKeys, newFilterDeSelectedRecordingKeys);

      return {
        ...state,
        fetching: false,
        recordings: {
          ...state.recordings,
          byKey,
          allKeys,
          filteredKeys: filteredKeys,
          filteredSelectedRecordingKeys: newFilterSelectedRecordingKeys
        },
        filterRecordings: {
          ...state.filterRecordings,
          byKey,
          allKeys,
          filteredKeys: filteredKeys,
          deSelectedKeys: newFilterDeSelectedRecordingKeys,
          filteredSelectedRecordingKeys: newFilterDeSelectedRecordingKeysSecond
        },
        filter: {
          ...state.filter,
          fromTime,
          toTime
        },
        filterRecordingStats: {
          ...state.filterRecordingStats,
          filteredTotalVideos: filteredStats.totalVideos,
          filteredG2STotalVideos: filteredStats.totalG2SVideos,
          filteredStandaloneTotalVideos: filteredStats.totalStandaloneVideos,
          filteredG2STotalViews: filteredStats.g2sViews,
          filteredStandaloneTotalViews: filteredStats.standaloneViews,
          filteredG2STotalShares: filteredStats.g2sShares
        },
        stats: {
          ...state.stats,
          filteredTotalVideos: filteredStats.totalVideos,
          filteredG2STotalVideos: filteredStats.totalG2SVideos,
          filteredStandaloneTotalVideos: filteredStats.totalStandaloneVideos,
          filteredG2STotalViews: filteredStats.g2sViews,
          filteredStandaloneTotalViews: filteredStats.standaloneViews,
          filteredG2STotalShares: filteredStats.g2sShares,
          filteredSelectedTotalVideos: filteredSelectedStats.totalVideos,
          filteredSelectedG2STotalVideos: filteredSelectedStats.totalG2SVideos,
          filteredSelectedStandaloneTotalVideos: filteredSelectedStats.totalStandaloneVideos,
          filteredSelectedG2STotalViews: filteredSelectedStats.g2sViews,
          filteredSelectedStandaloneTotalViews: filteredSelectedStats.standaloneViews,
          filteredSelectedG2STotalShares: filteredSelectedStats.g2sShares
        }
      };
    }

    case GET_RECORDINGS_FAILURE:
      return { ...state, fetching: false, error: action.error };

    case TOGGLE_RECORDING: {
      const { recordingKey, isSelected } = action.payload;

      const computedRecording = getCurrentRecording(state);
      const data = computedRecording.currentSession;
      const filterReportFlag = computedRecording.recordingsTableInReportsFlag;

      const { deSelectedKeys: existingDeSelectedKeys, allKeys, filteredKeys } = data;
      const newDeSelectedKeys = isSelected
        ? existingDeSelectedKeys.filter(key => key !== recordingKey)
        : sortArrayBasedOnArray([...existingDeSelectedKeys, recordingKey], allKeys);
      const newFilterSelectedRecordingKeys = getDifference(filteredKeys, newDeSelectedKeys);
      return {
        ...state,
        recordings: {
          ...state.recordings,
          deSelectedKeys: filterReportFlag ? state.recordings.deSelectedKeys : newDeSelectedKeys,
          filteredSelectedRecordingKeys: filterReportFlag
            ? state.recordings.filteredSelectedRecordingKeys
            : newFilterSelectedRecordingKeys
        },
        filterRecordings: {
          ...state.filterRecordings,
          deSelectedKeys: filterReportFlag ? newDeSelectedKeys : state.filterRecordings.deSelectedKeys,
          filteredSelectedRecordingKeys: filterReportFlag
            ? newFilterSelectedRecordingKeys
            : state.filterRecordings.filteredSelectedRecordingKeys
        },
        filterRecordingStats: {
          ...state.filterRecordingStats,
          ...(filterReportFlag
            ? getUpdatedStatsOnToggleRecordingKey(state.filterRecordingStats, isSelected, data.byKey[recordingKey])
            : state.filterRecordingStats)
        },
        stats: {
          ...state.stats,
          ...(filterReportFlag
            ? state.stats
            : getUpdatedStatsOnToggleRecordingKey(state.stats, isSelected, data.byKey[recordingKey]))
        }
      };
    }

    case TOGGLE_ALL_RECORDINGS: {
      const computedRecording = getCurrentRecording(state);
      const data = computedRecording.currentSession;
      const filterReportFlag = computedRecording.recordingsTableInReportsFlag;

      const { deSelectedKeys: existingDeSelectedKeys, filteredKeys: existingFilteredKeys } = data;
      const newDeSelectedKeys = action.payload.isSelected
        ? getDifference(existingDeSelectedKeys, existingFilteredKeys)
        : getUnion(existingDeSelectedKeys, existingFilteredKeys);
      const newFilterSelectedRecordingKeys = getDifference(existingFilteredKeys, newDeSelectedKeys);

      return {
        ...state,
        recordings: {
          ...state.recordings,
          deSelectedKeys: filterReportFlag ? state.recordings.deSelectedKeys : newDeSelectedKeys,
          filteredSelectedRecordingKeys: filterReportFlag
            ? state.recordings.filteredSelectedRecordingKeys
            : newFilterSelectedRecordingKeys
        },
        filterRecordings: {
          ...state.filterRecordings,
          deSelectedKeys: filterReportFlag ? newDeSelectedKeys : state.filterRecordings.deSelectedKeys,
          filteredSelectedRecordingKeys: filterReportFlag
            ? newFilterSelectedRecordingKeys
            : state.filterRecordings.filteredSelectedRecordingKeys
        },
        stats: {
          ...state.stats,
          ...(filterReportFlag
            ? state.stats
            : getUpdatedStatsOnToggleAllRecordingKeys(state.stats, action.payload.isSelected))
        },
        filterRecordingStats: {
          ...state.filterRecordingStats,
          ...(filterReportFlag
            ? getUpdatedStatsOnToggleAllRecordingKeys(state.filterRecordingStats, action.payload.isSelected)
            : state.filterRecordings)
        }
      };
    }

    case FILTER_RECORDINGS_BY_COUNT_REQUEST: {
      return {
        ...state,
        filtering: true,
        error: null
      };
    }

    case FILTER_RECORDINGS_BY_COUNT: {
      const {
        viewsRange = state.filter.viewsRange,
        sharesRange = state.filter.sharesRange,
        searchString = state.filter.searchString,
        sources = state.filter.sources
      } = action.payload;

      const { allKeys, byKey, deSelectedKeys } = state.recordings;
      const filteredKeys = getFilteredRecordingKeys(byKey, viewsRange, sharesRange, searchString, sources, allKeys);
      const newFilterSelectedRecordingKeys = getDifference(filteredKeys, deSelectedKeys);
      const filteredStats = computeRecordingStats(byKey, filteredKeys);
      const filteredSelectedStats = computeRecordingStats(byKey, newFilterSelectedRecordingKeys);

      const filterAllKeys = state.filterRecordings.allKeys;
      const filterByKey = state.filterRecordings.byKey;
      const filterDeSelectedKeys = state.filterRecordings.deSelectedKeys;
      const filteredKeysSecond = getFilteredRecordingKeys(
        filterByKey,
        viewsRange,
        sharesRange,
        searchString,
        sources,
        filterAllKeys
      );
      const newFilterSelectedRecordingKeysSecond = getDifference(filteredKeysSecond, filterDeSelectedKeys);
      const filteredStatsSecond = computeRecordingStats(filterByKey, filteredKeysSecond);
      const filteredSelectedStatsSecond = computeRecordingStats(filterByKey, newFilterSelectedRecordingKeysSecond);

      return {
        ...state,
        filtering: false,
        recordings: {
          ...state.recordings,
          filteredKeys: filteredKeys,
          filteredSelectedRecordingKeys: newFilterSelectedRecordingKeys
        },
        filterRecordings: {
          ...state.filterRecordings,
          filteredKeys: filteredKeysSecond,
          filteredSelectedRecordingKeys: newFilterSelectedRecordingKeysSecond
        },
        filter: {
          ...state.filter,
          searchString: searchString,
          viewsRange: viewsRange,
          sharesRange: sharesRange,
          sources: sources
        },
        filterRecordingStats: {
          ...state.filterRecordingStats,
          filteredTotalVideos: filteredStatsSecond.totalVideos,
          filteredStandaloneTotalVideos: filteredStatsSecond.totalStandaloneVideos,
          filteredStandaloneTotalViews: filteredStatsSecond.standaloneViews,
          filteredSelectedTotalVideos: filteredSelectedStatsSecond.totalVideos,
          filteredSelectedStandaloneTotalVideos: filteredSelectedStatsSecond.totalStandaloneVideos,
          filteredSelectedStandaloneTotalViews: filteredSelectedStatsSecond.standaloneViews
        },
        stats: {
          ...state.stats,
          filteredTotalVideos: filteredStats.totalVideos,
          filteredG2STotalVideos: filteredStats.totalG2SVideos,
          filteredStandaloneTotalVideos: filteredStats.totalStandaloneVideos,
          filteredG2STotalViews: filteredStats.g2sViews,
          filteredStandaloneTotalViews: filteredStats.standaloneViews,
          filteredG2STotalShares: filteredStats.g2sShares,
          filteredSelectedTotalVideos: filteredSelectedStats.totalVideos,
          filteredSelectedG2STotalVideos: filteredSelectedStats.totalG2SVideos,
          filteredSelectedStandaloneTotalVideos: filteredSelectedStats.totalStandaloneVideos,
          filteredSelectedG2STotalViews: filteredSelectedStats.g2sViews,
          filteredSelectedStandaloneTotalViews: filteredSelectedStats.standaloneViews,
          filteredSelectedG2STotalShares: filteredSelectedStats.g2sShares
        }
      };
    }
    default:
      return state;
  }
};



// WEBPACK FOOTER //
// ./src/Reducks/Recordings/RecordingsReducer.js